import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uniq } from 'lodash';

//styles

//libraries
import * as creditAppSelectors from '@makemydeal/dr-dash-store/selectors/creditAppSelectors';
import * as creditAppActionCreators from '@makemydeal/dr-dash-store/actions/creditAppActionCreators';
import * as deskingActionCreators from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as pendingDealerReviewSelectors from '@makemydeal/dr-dash-store/selectors/pendingDealerReviewSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as multiScenarioSelectors from '@makemydeal/dr-dash-store/selectors/multiScenarioSelectors';
import * as pushToDarwinSelectors from '@makemydeal/dr-dash-store/selectors/pushToDarwinSelectors';
import * as dealLifecycleSelectors from '@makemydeal/dr-dash-store/selectors/dealLifecycleSelectors';
import * as dealHistorySelectors from '@makemydeal/dr-dash-store/selectors/dealHistorySelectors';
import * as dealHistoryActionCreators from '@makemydeal/dr-dash-store/actions/dealHistoryActionCreator';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as unsavedChangesSelectors from '@makemydeal/dr-dash-store/selectors/unsavedChangesSelectors';
import { logNewRelicPageAction, navigateTo } from '@makemydeal/dr-activities-common';
import { RouteValues } from '@makemydeal/dr-dash-store/routeConstants';

//components
import {
    UPDATED_DEAL_BANNER_LABELS,
    SOURCE_CUSTOMER,
    SOURCE_DARWIN,
    COMMON_CONSUMER_BANNER_TITLE,
    COMMON_CONSUMER_BANNER_MESSAGE,
    CREDIT_APP_UPDATED_BANNER_TITLE,
    CREDIT_APP_UPDATED_BANNER_MESSAGE
} from '../constants';
import { Box } from '@interstate/components/Box';
import { Alert } from '@interstate/components/Alert';
import { Action } from '@interstate/components/Action';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import ReloadModal from '../reloadModal/ReloadModal';
import { isSalesCheckInName, SALES_CHECK_IN_APPLICATION_NAME } from '@makemydeal/dr-shared-types';
import { usePendingSciUpdate } from '../dealerReview/usePendingSciUpdate';

const RELOAD_MESSAGE_FROM_DARWIN = 'Please reload to view latest Deal version.';
const RELOAD_MESSAGE_FROM_CREDIT_APP =
    'The customer has submitted their credit app or cash order. Please reload to view latest Deal version.';
const NOTIFY_MESSAGE_FROM_CONTRACT_SIGN_DATE =
    'The contract is fully signed, and the deal status has automatically updated from In Progress to Sold. You’re now ready to move to the next step.';
const NOTIFY_MESSAGE_FROM_ACCEPTED_BY_BUYER = 'The offer has been accepted!';
const BANNER_BOX_SX = { padding: '1.2rem 1rem', maxWidth: '1920px', margin: 'auto' };
const UpdatedDealBanner = () => {
    const dispatch = useDispatch();
    const [showBanner, setShowBanner] = useState(false);
    const [lastPersona, setLastPersona] = useState('');
    const [lastMilestones, setLastMilestones] = useState([] as string[]);
    const sciUpdate = usePendingSciUpdate();
    const socketEventNotification = useSelector(offerInfoSelectors.getSocketEventNotification);
    const [showReloadModal, setShowReloadModal] = useState(false);
    const updatedMilestones = useSelector(offerInfoSelectors.getSocketEventUpdatedMilestones);
    const isPaymentUpdate = useSelector(offerInfoSelectors.getSocketEventIsPaymentUpdate);
    const persona = useSelector(offerInfoSelectors.getSocketEventAppName);
    const isCommonConsumerSourced = useSelector(offerInfoSelectors.getIsSocketEventCommonConsumerSourced);
    const [reloadSource, setReloadSource] = useState('');
    const [reloadMessage, setReloadMessage] = useState('');
    const hasCreditAppIdUpdated = useSelector(creditAppSelectors.getHasCreditAppIdUpdated);
    const hasCreditAppUpdatedByParticipantsSave = useSelector(creditAppSelectors.getHasCreditAppUpdatedByParticipantsSave);
    const hasNewDealVersionCreatedByDarwin = useSelector(pushToDarwinSelectors.getHasNewDealVersionCreatedByDarwin);
    const hasContractSignedDateUpdated = useSelector(dealLifecycleSelectors.getHasContractSignDateUpdated);
    const dealLifecycle = useSelector(dealLifecycleSelectors.getDealLifecycle);
    const isContractSignedBannerShown = useRef(false);

    // Direct-load feature selectors
    const isDealHistoryLoading = useSelector(dealHistorySelectors.getDealHistoryIsLoading);
    const isPendingReviewDetecting = useSelector(dealHistorySelectors.isPendingReviewDetecting);
    const hasPriorMvVersion = useSelector(dealHistorySelectors.hasPriorMvPublishedVersion);
    const socketComment = useSelector(offerInfoSelectors.getSocketEventComment);
    const acceptedByBuyer = useSelector(offerInfoSelectors.getSocketEventAcceptedByBuyer);
    const socketVersionApplicationName = useSelector(pendingDealerReviewSelectors.getSocketVersionApplicationName);
    const hasUnsavedChanges = useSelector(unsavedChangesSelectors.currentOfferHasUnsavedChanges);
    const makeCurrentSuccess = useSelector(dealHistorySelectors.getMakeCurrentSuccess);
    const [showApplySuccess, setShowApplySuccess] = useState(false);

    const applicationName = useSelector(offerSelectors.getApplicationName);
    const messageToDealer = useSelector(offerReduxSelectors.getMessage);
    const isOfferCommentBannerEligible = useSelector(compositeSelectors.getIsOfferCommentBannerEligible);
    const isMoreRecentVersionAvailable = useSelector(multiScenarioSelectors.isMoreRecentVersionAvailable);
    const gtlBannerShownOnLoad = useSelector(offerInfoSelectors.getGtlBannerShownOnLoad);
    const isPendingDealerReview = useSelector(pendingDealerReviewSelectors.isPendingDealerReview);
    const [isLoadBanner, setIsLoadBanner] = useState(false);

    // New Relic telemetry selectors
    const sessionId = useSelector(offerReduxSelectors.getSessionId);
    const traceId = useSelector(offerReduxSelectors.getTraceId);
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const dealerName = useSelector(dealerSelectors.getDealerName);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);

    const logGTLPopupEvent = (eventName: string, additionalAttributes: Record<string, unknown> = {}) => {
        logNewRelicPageAction(eventName, {
            sessionId,
            traceId,
            mmdDealerId: dealerId,
            mmdDealerName: dealerName,
            dealXgId,
            ...additionalAttributes
        });
    };

    const isContractSold = dealLifecycle.status === 'Sold' && hasContractSignedDateUpdated;
    const shouldShowLoadBanner =
        !gtlBannerShownOnLoad && isOfferCommentBannerEligible && !isMoreRecentVersionAvailable && !isPendingDealerReview;
    const loadBannerSourceLabel = applicationName;

    const milestoneLabels = updatedMilestones.reduce((prev, current) => {
        const label = UPDATED_DEAL_BANNER_LABELS[current];
        if (label) prev.push(label);
        return prev;
    }, [] as string[]);

    const handleReloadModal = (source: string, message: string) => {
        setReloadSource(source);
        setReloadMessage(message);
        setShowReloadModal(true);
    };

    useEffect(() => {
        if (hasCreditAppIdUpdated) {
            handleReloadModal(SOURCE_CUSTOMER, RELOAD_MESSAGE_FROM_CREDIT_APP);
            dispatch(deskingActionCreators.setForceReloadOnWSEvent(true, SOURCE_CUSTOMER, RELOAD_MESSAGE_FROM_CREDIT_APP));
        } else if (hasNewDealVersionCreatedByDarwin) {
            handleReloadModal(SOURCE_DARWIN, RELOAD_MESSAGE_FROM_DARWIN);
            dispatch(deskingActionCreators.setForceReloadOnWSEvent(true, SOURCE_DARWIN, RELOAD_MESSAGE_FROM_DARWIN));
        }
    }, [hasCreditAppIdUpdated, hasNewDealVersionCreatedByDarwin]);

    // Pick up success signal from Offer History "Make Current" flow
    useEffect(() => {
        if (makeCurrentSuccess) {
            dispatch(dealHistoryActionCreators.clearMakeCurrentSuccess());
            setShowApplySuccess(true);
        }
    }, [makeCurrentSuccess]);

    useEffect(() => {
        if (shouldShowLoadBanner) {
            dispatch(offerActionCreators.markGtlBannerShownOnLoad());
            setIsLoadBanner(true);
            setShowBanner(true);
            logGTLPopupEvent('MV.GTLPopup.Displayed', { hasComment: true, source: 'bootstrap' });
        }
    }, [shouldShowLoadBanner]);

    useEffect(() => {
        if (socketEventNotification) {
            // a live WS event supersedes any load-derived banner
            setIsLoadBanner(false);
            setLastPersona(persona);
            setLastMilestones((prev) => uniq(prev.concat(milestoneLabels)));
            setShowBanner(true);

            logGTLPopupEvent('MV.GTLPopup.Displayed', { hasComment: !!socketComment });
        }
    }, [socketEventNotification]);

    useEffect(() => {
        if (!isContractSignedBannerShown.current && isContractSold) {
            setShowBanner(true);
            isContractSignedBannerShown.current = true;
        }
    }, [hasContractSignedDateUpdated, isContractSold]);

    const handleBanner = () => {
        logGTLPopupEvent('MV.GTLPopup.OkGotIt.Clicked');
        setShowBanner(false);
        setIsLoadBanner(false);
        setLastPersona('');
        setLastMilestones([]);
        sciUpdate.reset();
        dispatch(offerActionCreators.clearSocketEventNotification());
    };

    const { isPendingSocketUpdate, showChangedFields: showPendingChangedFields } = sciUpdate;
    // Common Consumer republishes the same customer change seconds later as a system version. DE768436.
    const showCommonConsumerCopy = isCommonConsumerSourced && !showPendingChangedFields;
    const isAcceptedByBuyerFromSalesCheckin = acceptedByBuyer && isSalesCheckInName(socketVersionApplicationName);
    const resolutionTargetVersion = sciUpdate.targetVersion;

    const updateSourceLabel = isLoadBanner
        ? loadBannerSourceLabel
        : sciUpdate.isSalesCheckInAttributed
        ? SALES_CHECK_IN_APPLICATION_NAME
        : lastPersona;

    const handleApplyChanges = () => {
        logGTLPopupEvent('MV.GTLPopup.ApplyChanges.Clicked');
        if (hasUnsavedChanges) {
            dispatch(offerActionCreators.saveOffer({ isDraft: true, source: 'updatedDealBanner' }));
        }
        // Intent only: the sequence differs by trigger and `socketVersion` is '' on load, so
        // makeCurrentDeal here would re-initialise the already-loaded version.
        if (isPendingDealerReview || isPendingSocketUpdate) {
            dispatch(offerActionCreators.resolvePendingReviewRequested());
        } else {
            dispatch(dealHistoryActionCreators.makeCurrentDeal(resolutionTargetVersion || undefined));
        }
        dispatch(offerActionCreators.clearSocketEventNotification());
        setShowBanner(false);
        sciUpdate.reset();
        setShowApplySuccess(true);
    };

    const handleDismissCreditAppUpdated = () => {
        dispatch(creditAppActionCreators.dismissCreditAppUpdatedAlert());
    };

    const handleReviewNow = () => {
        logGTLPopupEvent('MV.GTLPopup.ReviewNow.Clicked');
        const path = resolutionTargetVersion
            ? `${RouteValues.DEAL_HISTORY}?targetDealXgVersion=${encodeURIComponent(resolutionTargetVersion)}`
            : RouteValues.DEAL_HISTORY;
        if (isPendingDealerReview) {
            // Navigation applies nothing; this arms the post-apply reactor for the dealer's make-current.
            dispatch(offerActionCreators.resolvePendingReviewRequested());
        }
        dispatch(navigateTo(path));
        handleBanner();
    };

    const alertInfo = () => (
        <div data-testid="banner-info-container">
            {isContractSold ? (
                NOTIFY_MESSAGE_FROM_CONTRACT_SIGN_DATE
            ) : showCommonConsumerCopy ? (
                <span data-testid="common-consumer-generic-message">{COMMON_CONSUMER_BANNER_MESSAGE}</span>
            ) : showPendingChangedFields ? (
                <span data-testid="pending-review-changed-fields">
                    {sciUpdate.sender ? `${sciUpdate.sender} made updates to: ` : 'This offer has updates to: '}
                    {sciUpdate.labels.map((label, index) => (
                        <Fragment key={label}>
                            {index !== 0 && (sciUpdate.labels.length - 1 === index ? ' and ' : ', ')}
                            <b>{label}</b>
                        </Fragment>
                    ))}
                    .
                    {sciUpdate.freeTextComment ? (
                        <span data-testid="pending-review-socket-comment"> {sciUpdate.freeTextComment}</span>
                    ) : isCommonConsumerSourced && !socketComment ? (
                        <span data-testid="pending-review-common-consumer-message"> {COMMON_CONSUMER_BANNER_MESSAGE}</span>
                    ) : null}
                </span>
            ) : isAcceptedByBuyerFromSalesCheckin ? (
                NOTIFY_MESSAGE_FROM_ACCEPTED_BY_BUYER
            ) : isLoadBanner ? (
                <span>{messageToDealer}</span>
            ) : socketComment ? (
                <span>{socketComment}</span>
            ) : lastMilestones.length > 0 ? (
                <span>
                    This offer has updates to:{' '}
                    {lastMilestones.map((label, index) => (
                        <Fragment key={index}>
                            {index !== 0 && (lastMilestones.length - 1 === index ? ' and ' : ', ')}
                            <b>{label}</b>
                        </Fragment>
                    ))}
                    .{isPaymentUpdate ? " See what's changed via Quick Compare." : ''}
                </span>
            ) : (
                <span>{'This offer has updates.'}</span>
            )}
        </div>
    );

    // While deal history is still loading, suppress Updated Deal banner
    const shouldSuppressBanner = (isDealHistoryLoading || isPendingReviewDetecting) && !isContractSold && !showReloadModal;

    return (
        <>
            {showApplySuccess && (
                <Box sx={BANNER_BOX_SX}>
                    <Alert
                        data-testid="apply-changes-success-banner"
                        title="Changes Applied"
                        type="success"
                        displayCloseButton
                        onCloseClick={() => setShowApplySuccess(false)}
                    >
                        <span>The deal has been successfully updated. Your previous version has been saved as a draft.</span>
                    </Alert>
                </Box>
            )}
            {hasCreditAppUpdatedByParticipantsSave && (
                <Box sx={BANNER_BOX_SX}>
                    <Alert
                        data-testid="credit-app-updated-banner"
                        title={CREDIT_APP_UPDATED_BANNER_TITLE}
                        type="caution"
                        displayCloseButton
                        onCloseClick={handleDismissCreditAppUpdated}
                    >
                        <div>{CREDIT_APP_UPDATED_BANNER_MESSAGE}</div>
                        <div>
                            <Action
                                data-testid="credit-app-updated-dismiss-action"
                                size="sm"
                                onClick={handleDismissCreditAppUpdated}
                            >
                                <span style={{ textDecoration: 'underline' }}>Okay, Got It</span>
                            </Action>
                        </div>
                    </Alert>
                </Box>
            )}
            {showBanner &&
                !shouldSuppressBanner &&
                (showReloadModal ? (
                    <ReloadModal reloadMessage={reloadMessage} reloadSource={reloadSource} />
                ) : (
                    <Box sx={BANNER_BOX_SX}>
                        <Alert
                            data-testid="interstate-banner-container"
                            title={
                                isContractSold
                                    ? 'Your deal has been marked as SOLD'
                                    : showCommonConsumerCopy
                                    ? COMMON_CONSUMER_BANNER_TITLE
                                    : `Updates from ${updateSourceLabel}`
                            }
                            type={isContractSold ? 'success' : 'caution'}
                            displayCloseButton
                            onCloseClick={handleBanner}
                        >
                            {alertInfo()}
                            <div>
                                {!isContractSold && !isLoadBanner && (
                                    <>
                                        {hasPriorMvVersion && !isPendingSocketUpdate && !isAcceptedByBuyerFromSalesCheckin ? (
                                            <>
                                                <span style={{ marginRight: '0.5rem' }}>
                                                    <Action data-testid="review-now-action" size="sm" onClick={handleReviewNow}>
                                                        <span style={{ textDecoration: 'underline' }}>Review now</span>
                                                    </Action>
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <span style={{ marginRight: '0.5rem' }}>
                                                    <Action
                                                        data-testid="apply-changes-action"
                                                        size="sm"
                                                        onClick={handleApplyChanges}
                                                    >
                                                        <span style={{ textDecoration: 'underline' }}>Apply changes</span>
                                                    </Action>
                                                </span>
                                            </>
                                        )}
                                    </>
                                )}
                                <Action data-testid="interstate-hide-banner-action" size="sm" onClick={handleBanner}>
                                    {!isContractSold ? (
                                        <span style={{ textDecoration: 'underline' }}>Okay, Got It</span>
                                    ) : (
                                        'Okay, Got It'
                                    )}
                                </Action>
                            </div>
                        </Alert>
                    </Box>
                ))}
        </>
    );
};

export default UpdatedDealBanner;
